import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppImage from '../../../components/AppImage';
import { deleteSong } from '../../../services/songService';
import { useAuth } from '../../../contexts/AuthContext';

const SongListItem = ({ song, onUpdate, onClick, onToggleFavorite, isFavorited, showFavoriteButton, isFeatured, onFeatureToggle, viewMode = 'full' }) => {
  const navigate = useNavigate();
  const { user, isSuperUser } = useAuth();

  // Handle song data properly with optional chaining
  const songTitle = song?.title || 'Untitled Song';
  const musicalTitle = song?.musical?.title || 'Unknown Musical';
  const musicalId = song?.musical?.id;
  const musicalPosterUrl = song?.musical?.poster_url;
  const composer = song?.musical?.composer || 'Unknown Composer';
  const lyricist = song?.musical?.lyricist || 'Unknown Lyricist';
  const yearPremiered = song?.musical?.year_premiered;
  const genre = song?.musical?.genre || 'Unknown';
  const actNumber = song?.act_number;
  const sceneNumber = song?.scene_number;
  const songOrder = song?.song_order;
  const tempo = song?.tempo;
  const keySignature = song?.key_signature;
  const timeSignature = song?.time_signature;
  const duration = song?.duration_seconds;
  
  // Character analysis content data
  const characterRolesCount = song?.character_roles_count || 0;
  const lessonsCount = song?.lessons_count || 0;
  const hasRecentLessons = song?.has_recent_lessons || false;
  const videosCount = song?.videos_count || 0;
  const hasMidi = !!(song?.midi_file_path);
  const hasMp3 = !!(song?.mp3_file_path);
  
  // Format duration from seconds to mm:ss
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  // Format position info into a concise string
  const formatPosition = () => {
    const parts = [];
    if (actNumber) parts?.push(`Act ${actNumber}`);
    if (sceneNumber) parts?.push(`Scene ${sceneNumber}`);
    if (songOrder) parts?.push(`Song #${songOrder}`);
    return parts?.join(', ') || 'Position N/A';
  };

  // Handle navigation to musical detail page
  const handleMusicalClick = (e) => {
    e?.stopPropagation();
    if (musicalId) {
      navigate(`/add-musical?id=${musicalId}`);
    }
  };

  // Handle navigation to song detail for editing
  const handleViewDetails = () => {
    if (onClick) {
      onClick(song);
    } else {
      // Navigate to edit details page
      navigate(`/song-detail-page?id=${song?.id}`);
    }
  };

  const handleFavoriteClick = (e) => {
    e?.stopPropagation();
    onToggleFavorite?.(song?.id);
  };

  // Handle delete functionality
  const handleDelete = async (e) => {
    e?.stopPropagation();
    
    if (!isSuperUser()) {
      alert('You do not have permission to delete songs');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${songTitle}" from ${musicalTitle}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const { error } = await deleteSong(song?.id);
      
      if (error) {
        alert(`Failed to delete song: ${error}`);
        return;
      }

      // Refresh the song list
      if (onUpdate) {
        onUpdate();
      }
      
      alert('Song deleted successfully');
    } catch (err) {
      console.error('Error deleting song:', err);
      alert('Failed to delete song. Please try again.');
    }
  };

  // Compact view component
  if (viewMode === 'compact') {
    return (
      <div 
        className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-3 hover:bg-opacity-15 transition-all duration-200 cursor-pointer relative"
        onClick={handleViewDetails}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Poster thumbnail and basic info */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Compact Poster */}
            <div 
              className="flex-shrink-0 w-10 h-12 bg-white bg-opacity-20 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-white hover:ring-opacity-50 transition-all duration-200"
              onClick={handleMusicalClick}
              title={`View ${musicalTitle} details`}
            >
              {musicalPosterUrl ? (
                <AppImage
                  src={musicalPosterUrl}
                  alt={`${musicalTitle} poster`}
                  className="w-full h-full object-cover"
                  fallback="/assets/images/no_image.png"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white text-opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Song info - single line */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 text-sm">
                <span className="font-semibold text-white truncate">{songTitle}</span>
                <span className="text-blue-300">•</span>
                <button
                  onClick={handleMusicalClick}
                  className="text-blue-200 hover:text-white hover:underline transition-colors truncate"
                  title={`View ${musicalTitle} details`}
                >
                  {musicalTitle}
                </button>
                
                {yearPremiered && (
                  <>
                    <span className="text-blue-300">•</span>
                    <span className="text-blue-200 flex-shrink-0">{yearPremiered}</span>
                  </>
                )}

                {characterRolesCount > 0 && (
                  <>
                    <span className="text-blue-300">•</span>
                    <span className="text-blue-200 flex-shrink-0">{characterRolesCount} role{characterRolesCount !== 1 ? 's' : ''}</span>
                  </>
                )}

                {isFeatured && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-500 bg-opacity-20 text-yellow-200 border border-yellow-400 border-opacity-30 flex-shrink-0">
                    Featured
                  </span>
                )}
              </div>

              {/* Media indicators - compact row */}
              <div className="flex items-center space-x-2 mt-1">
                {/* Video indicator */}
                <span
                  title={videosCount > 0 ? `${videosCount} video${videosCount !== 1 ? 's' : ''} uploaded` : 'No videos uploaded'}
                  className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                    videosCount > 0
                      ? 'bg-purple-500 bg-opacity-25 text-purple-200 border border-purple-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-25 border border-white border-opacity-10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>{videosCount > 0 ? videosCount : '0'}</span>
                </span>

                {/* MIDI indicator */}
                <span
                  title={hasMidi ? 'MIDI file uploaded' : 'No MIDI file'}
                  className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                    hasMidi
                      ? 'bg-green-500 bg-opacity-25 text-green-200 border border-green-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-25 border border-white border-opacity-10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM4 7h16" />
                  </svg>
                  <span>MIDI</span>
                </span>

                {/* MP3 indicator */}
                <span
                  title={hasMp3 ? 'MP3 file uploaded' : 'No MP3 file'}
                  className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                    hasMp3
                      ? 'bg-blue-500 bg-opacity-25 text-blue-200 border border-blue-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-25 border border-white border-opacity-10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 000 7.072M12 9.5l-3 3H6v-2.828l8.586-8.586z" />
                  </svg>
                  <span>MP3</span>
                </span>

                {/* Lessons indicator */}
                <span
                  title={lessonsCount > 0 ? `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} added` : 'No lessons added'}
                  className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                    lessonsCount > 0
                      ? 'bg-yellow-500 bg-opacity-25 text-yellow-200 border border-yellow-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-25 border border-white border-opacity-10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>{lessonsCount > 0 ? lessonsCount : '0'}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Star button - superuser only */}
            {onFeatureToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFeatureToggle(song); }}
                className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
              >
                <svg
                  className={`w-4 h-4 ${isFeatured ? 'text-yellow-400' : 'text-blue-300 opacity-60'}`}
                  fill={isFeatured ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}

            {/* Favorite button */}
            {showFavoriteButton && (
              <button
                onClick={handleFavoriteClick}
                className={`p-1.5 rounded transition-colors ${
                  isFavorited 
                    ? 'text-red-400 hover:text-red-300 bg-red-500 bg-opacity-20' :'text-white text-opacity-60 hover:text-red-400 hover:bg-white hover:bg-opacity-10'
                }`}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg 
                  className="w-4 h-4" 
                  fill={isFavorited ? 'currentColor' : 'none'} 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                  />
                </svg>
              </button>
            )}

            {/* Edit button */}
            <button
              onClick={handleViewDetails}
              className="p-1.5 text-white text-opacity-60 hover:text-white hover:bg-white hover:bg-opacity-10 rounded transition-colors"
              title="Edit details"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            {/* Delete button - only for SuperUsers */}
            {isSuperUser() && (
              <button
                onClick={handleDelete}
                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500 hover:bg-opacity-20 rounded transition-all duration-200"
                title="Delete song"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full view (existing implementation)
  return (
    <div 
      className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 hover:bg-opacity-15 transition-all duration-300 cursor-pointer relative"
      onClick={handleViewDetails}
    >
      <div className="flex items-start space-x-6">
        {/* Musical Poster Thumbnail - Now clickable */}
        <div 
          className="flex-shrink-0 w-16 h-20 bg-white bg-opacity-20 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white hover:ring-opacity-50 transition-all duration-200"
          onClick={handleMusicalClick}
          title={`View ${musicalTitle} details`}
        >
          {musicalPosterUrl ? (
            <AppImage
              src={musicalPosterUrl}
              alt={`${musicalTitle} poster`}
              className="w-full h-full object-cover"
              fallback="/assets/images/no_image.png"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white text-opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Song Content */}
        <div className="flex-1 min-w-0">
          {/* Song Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-heading font-semibold text-white mb-1 flex items-center">
                <span className="truncate">{songTitle}</span>
                {isFeatured && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 bg-opacity-20 text-yellow-200 border border-yellow-400 border-opacity-30">
                    Featured
                  </span>
                )}
                {hasRecentLessons && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500 bg-opacity-20 text-green-200 border border-green-400 border-opacity-30">
                    Recently Updated
                  </span>
                )}
              </h3>
              <p className="text-blue-200 font-body text-sm mb-2">
                from{' '}
                {/* Make Musical Title Clickable */}
                <button
                  onClick={handleMusicalClick}
                  className="font-medium text-white hover:text-blue-100 hover:underline transition-colors duration-200 focus:outline-none focus:underline"
                  title={`View ${musicalTitle} details`}
                >
                  {musicalTitle}
                </button>
                {yearPremiered && (
                  <span className="text-blue-300 ml-1">({yearPremiered})</span>
                )}
              </p>
            </div>

            {/* Favorite Button */}
            {showFavoriteButton && (
              <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                {/* Star button - superuser only */}
                {onFeatureToggle && (
                  <button
                    onClick={(e) => { e?.stopPropagation(); onFeatureToggle(song); }}
                    className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                    title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    <svg
                      className={`w-5 h-5 ${isFeatured ? 'text-yellow-400' : 'text-blue-300 opacity-60'}`}
                      fill={isFeatured ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={handleFavoriteClick}
                  className={`p-2 rounded-xl transition-colors ${
                    isFavorited 
                      ? 'text-red-400 hover:text-red-300 bg-red-500 bg-opacity-20' :'text-white text-opacity-60 hover:text-red-400 hover:bg-white hover:bg-opacity-10'
                  }`}
                  title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <svg 
                    className="w-5 h-5" 
                    fill={isFavorited ? 'currentColor' : 'none'} 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                    />
                  </svg>
                </button>
              </div>
            )}
            {!showFavoriteButton && onFeatureToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFeatureToggle(song); }}
                className="ml-4 p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors flex-shrink-0"
                title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
              >
                <svg
                  className={`w-5 h-5 ${isFeatured ? 'text-yellow-400' : 'text-blue-300 opacity-60'}`}
                  fill={isFeatured ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
          </div>

          {/* Media Content Indicators */}
          <div className="flex items-center flex-wrap gap-2 mb-3">
            {/* Video indicator */}
            <span
              title={videosCount > 0 ? `${videosCount} video${videosCount !== 1 ? 's' : ''} uploaded` : 'No videos uploaded'}
              className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                videosCount > 0
                  ? 'bg-purple-500 bg-opacity-25 text-purple-200 border border-purple-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-30 border border-white border-opacity-10'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.555.832l-3-2a1 1 0 000-1.664l3-2z" />
              </svg>
              <span>{videosCount > 0 ? `${videosCount} Video${videosCount !== 1 ? 's' : ''}` : 'No Videos'}</span>
            </span>

            {/* MIDI indicator */}
            <span
              title={hasMidi ? 'MIDI file uploaded' : 'No MIDI file uploaded'}
              className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                hasMidi
                  ? 'bg-green-500 bg-opacity-25 text-green-200 border border-green-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-30 border border-white border-opacity-10'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{hasMidi ? 'MIDI' : 'No MIDI'}</span>
            </span>

            {/* MP3 indicator */}
            <span
              title={hasMp3 ? 'MP3 file uploaded' : 'No MP3 file uploaded'}
              className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                hasMp3
                  ? 'bg-blue-500 bg-opacity-25 text-blue-200 border border-blue-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-30 border border-white border-opacity-10'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072M12 9.5l-3 3H6v3h3l3 3V9.5zm6.364-1.364a9 9 0 010 12.728" />
              </svg>
              <span>{hasMp3 ? 'MP3' : 'No MP3'}</span>
            </span>

            {/* Lessons indicator */}
            <span
              title={lessonsCount > 0 ? `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} added` : 'No lessons added'}
              className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                lessonsCount > 0
                  ? 'bg-yellow-500 bg-opacity-25 text-yellow-200 border border-yellow-400 border-opacity-40' :'bg-white bg-opacity-5 text-white text-opacity-30 border border-white border-opacity-10'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span>{lessonsCount > 0 ? `${lessonsCount} Lesson${lessonsCount !== 1 ? 's' : ''}` : 'No Lessons'}</span>
            </span>
          </div>

          {/* Character Analysis Stats */}
          {characterRolesCount > 0 && (
            <div className="mb-3">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-sm text-blue-200">
                    <span className="font-medium text-white">{characterRolesCount}</span> character role{characterRolesCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Condensed Song Information - All in One Line */}
          <div className="text-sm text-blue-200 mb-4">
            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
              {/* Position */}
              <span>
                <span className="font-medium text-white">Position:</span> {formatPosition()}
              </span>
              
              <span className="text-blue-300">•</span>
              
              {/* Composer */}
              <span>
                <span className="font-medium text-white">Composer:</span> {composer}
              </span>
              
              <span className="text-blue-300">•</span>
              
              {/* Lyricist - only show if different from composer */}
              {lyricist && lyricist !== composer && (
                <>
                  <span>
                    <span className="font-medium text-white">Lyricist:</span> {lyricist}
                  </span>
                  <span className="text-blue-300">•</span>
                </>
              )}
              
              {/* Genre */}
              <span>
                <span className="font-medium text-white">Genre:</span> 
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-blue-100 capitalize ml-1">
                  {genre}
                </span>
              </span>
              
              {/* Time signature and Duration */}
              {(timeSignature || duration) && (
                <>
                  <span className="text-blue-300">•</span>
                  <span>
                    <span className="font-medium text-white">Time:</span>
                    {timeSignature && <span className="ml-1">{timeSignature}</span>}
                    {duration && timeSignature && <span className="ml-1">({formatDuration(duration)})</span>}
                    {duration && !timeSignature && <span className="ml-1">{formatDuration(duration)}</span>}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-white border-opacity-20">
            <button
              onClick={handleViewDetails}
              className="text-white bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2"
            >
              <span>Edit Details</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            
            {/* Media Actions and Delete Button Container */}
            <div className="flex items-center space-x-2">
              {song?.audio_url && (
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    // Handle audio play logic here
                  }}
                  className="p-2 text-white text-opacity-60 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-xl transition-colors duration-200"
                  title="Play audio"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              
              {song?.sheet_music_url && (
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    window.open(song?.sheet_music_url, '_blank');
                  }}
                  className="p-2 text-white text-opacity-60 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-xl transition-colors duration-200"
                  title="View sheet music"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Icon - Bottom Right Corner */}
      {isSuperUser() && (
        <button
          onClick={handleDelete}
          className="absolute bottom-3 right-3 p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500 hover:bg-opacity-20 rounded-lg transition-all duration-200 group"
          title="Delete song"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default SongListItem;